/* ============================================================================
   מחזיק את המפתח הפרטי בזיכרון של הלשונית, ורק שם.

   הוא לא נשמר ב-localStorage ולא ב-sessionStorage: כל אחסון כזה הופך כל
   פרצת XSS לדליפה של כל צילומי תעודות הזהות שנשמרו אי פעם. המחיר הוא
   שרענון דף מבקש את הסיסמה שוב — אבל רק כשבאמת פותחים צילום ת"ז, וזה
   קורה נדיר.
   ============================================================================ */

import { api } from "./api.js";
import {
  createKeyMaterialOwnerId,
  decryptFile,
  encryptForAccount,
  dataUrlToBytes,
  normalizeOwnerId,
  resealWithPassword,
  sealWithOwnerId,
  unlockWithOwnerId,
  unlockWithPassword,
  unlockWithRecoveryCode,
  cryptoAvailable,
} from "./crypto.js";

let privateKey = null;      // CryptoKey, לא ניתן לייצוא מחוץ ל-WebCrypto
let stored = null;          // { publicKey, byPassword, byRecovery }
let loaded = false;
const listeners = new Set();

const notify = () => listeners.forEach((f) => f());

export const onVaultChange = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

export const isUnlocked = () => !!privateKey;
export const isAvailable = cryptoAvailable;

/** טוען את חומר המפתחות מהשרת. null = עוד לא הוקם לחשבון הזה. */
export async function loadKeys(force = false) {
  if (loaded && !force) return stored;
  try {
    stored = await api.get("/keys");
  } catch (err) {
    if (err.status === 404) stored = null;
    else throw err;
  }
  loaded = true;
  return stored;
}

export const hasKeys = () => !!stored;
export const publicKey = () => stored?.publicKey || null;

/**
 * המפתח הציבורי של בעל הנכס, אחרי טעינה מהשרת אם צריך.
 *
 * publicKey() הסינכרוני מחזיר null עד ש-loadKeys רץ — והוא רץ רק כשנפתח
 * מסך שמציג את מצב ההצפנה. מי שמעלה צילום (אשף, קליטה) חייב את הגרסה
 * הזו, אחרת חשבון עם הצפנה פעילה נראה כאילו אין לו מפתח.
 * null = ההצפנה באמת לא הופעלה לחשבון.
 */
export async function ownerPublicKey() {
  await loadKeys();
  return publicKey();
}

/**
 * המפתח הציבורי של בעל הנכס, כפי שהדייר מקבל אותו.
 * הדייר לא מחזיק מפתח פרטי — הוא רק מצפין אל בעל הנכס.
 */
let tenantPub;
export async function tenantPublicKey() {
  if (tenantPub !== undefined) return tenantPub;
  try {
    const r = await api.get("/keys/public");
    tenantPub = r.publicKey || null;
  } catch {
    tenantPub = null;
  }
  return tenantPub;
}

/**
 * הקמה ראשונית — עם תעודת הזהות של בעל הנכס, לא עם סיסמה. מחזיר את קוד
 * השחזור — יוצג פעם אחת ולא ניתן לשחזור; הוא הגיבוי למקרה של טעות בת"ז.
 */
export async function setupKeys(ownerId) {
  const id = normalizeOwnerId(ownerId);
  if (!id) throw new Error("צריך תעודת זהות כדי להקים את ההצפנה.");
  const { recoveryCode, privateKey: pk, stored: payload } = await createKeyMaterialOwnerId(id);
  await api.post("/keys", payload);
  stored = payload;
  loaded = true;
  privateKey = pk;
  notify();
  return recoveryCode;
}

/**
 * פתיחה עם מה שהמשתמש הקליד: קודם עטיפת תעודת הזהות (הכספות החדשות),
 * ואם לא — הסיסמה (כספות שהוקמו לפני 25.8.2026).
 */
export async function unlock(value) {
  await loadKeys();
  if (!stored) throw new Error("לא הוגדרה הצפנה לחשבון הזה.");
  if (stored.byOwnerId) {
    try {
      privateKey = await unlockWithOwnerId(stored, value);
      notify();
      return;
    } catch { /* אולי זו הסיסמה הישנה */ }
  }
  if (stored.byPassword) {
    try {
      privateKey = await unlockWithPassword(stored, value);
      notify();
      return;
    } catch {
      throw new Error(stored.byOwnerId ? "תעודת הזהות שגויה." : "הסיסמה שגויה.");
    }
  }
  throw new Error("תעודת הזהות שגויה.");
}

/**
 * ניסיון שקט לפתוח בתעודת הזהות של בעל הנכס — היא כבר בהגדרות החשבון,
 * ולכן בעל הנכס לא צריך להקליד דבר. לא זורק; מחזיר האם הכספת פתוחה.
 */
export async function autoUnlock(ownerId) {
  try {
    if (privateKey) return true;
    const id = normalizeOwnerId(ownerId);
    if (!id) return false;
    await loadKeys();
    if (!stored?.byOwnerId) return false;
    privateKey = await unlockWithOwnerId(stored, id);
    notify();
    return true;
  } catch {
    return false;
  }
}

/**
 * משדרג כספת ישנה: אחרי שהכספת נפתחה (בסיסמה או בקוד שחזור), עטיפת
 * תעודת הזהות נוספת לצידן — ומהפתיחה הבאה הת"ז תספיק. אידמפוטנטי.
 */
export async function ensureOwnerIdWrapping(ownerId) {
  const id = normalizeOwnerId(ownerId);
  if (!privateKey || !id || !stored || stored.byOwnerId) return;
  const byOwnerId = await sealWithOwnerId(privateKey, id);
  await api.put("/keys/owner-id", { byOwnerId });
  stored.byOwnerId = byOwnerId;
}

/** אילו עטיפות קיימות — כדי שדיאלוג הפתיחה ידע מה לבקש. */
export async function wrappings() {
  await loadKeys();
  return { ownerId: !!stored?.byOwnerId, password: !!stored?.byPassword };
}

export async function unlockByRecovery(code) {
  await loadKeys();
  if (!stored) throw new Error("לא הוגדרה הצפנה לחשבון הזה.");
  privateKey = await unlockWithRecoveryCode(stored, code);
  notify();
}

/**
 * אורז מחדש את המפתח הפרטי בסיסמה חדשה ומחזיר את החבילה.
 *
 * לא כותב לשרת: החלפת הסיסמה והאריזה מחדש חייבות להישמר יחד בטרנזקציה
 * אחת (/auth/password), אחרת מצב ביניים נועל את בעל הנכס מחוץ לצילומים.
 */
export async function resealFor(newPassword) {
  if (!privateKey) throw new Error("צריך לפתוח את כספת ההצפנה קודם.");
  return resealWithPassword(privateKey, newPassword);
}

/** מעדכן את העותק המקומי אחרי שהשרת אישר. */
export function noteResealed(byPassword) {
  if (stored) stored.byPassword = byPassword;
}

export function lock() {
  privateKey = null;
  notify();
}

/* ------------------------------------------------------------- שימוש --- */

/**
 * מצפין dataURL למפתח החשבון ומחזיר את מה שנשלח לשרת בהעלאה.
 * לא דורש כספת פתוחה — הצפנה צריכה רק את המפתח הציבורי.
 */
export async function sealForUpload(dataUrl, publicKeyB64) {
  const key = publicKeyB64 || publicKey();
  if (!key) throw new Error("לא הוגדרה הצפנה לחשבון הזה.");
  const { data, wrappedKey, iv } = await encryptForAccount(dataUrlToBytes(dataUrl), key);
  return { data, wrappedKey, iv };
}

/**
 * מצפין בדיעבד צילומי ת"ז שכבר יושבים בשרת בגלוי — למשל כאלה שהגיעו
 * מייבוא הגיבוי מהארטיפקט, שנוצר לפני שההצפנה קיימת.
 *
 * הקובץ מוחלף במזהה חדש, ולכן המסמך צריך להתעדכן. הפונקציה מחזירה מיפוי
 * ישן→חדש והקורא אחראי להחיל אותו (remapPhotoIds ב-App.jsx).
 */
export async function encryptExisting(ids, { onProgress } = {}) {
  const key = publicKey();
  if (!key) throw new Error("ההצפנה לא הוגדרה.");

  const { uploadImage } = await import("./api.js");
  const map = {};
  let done = 0;

  for (const id of ids) {
    try {
      const meta = await api.get(`/files/${encodeURIComponent(id)}/envelope`);
      if (meta.encrypted) continue; // כבר מוצפן

      const res = await fetch(`/api/files/${encodeURIComponent(id)}`, {
        credentials: "include",
      });
      if (!res.ok) continue;

      const blob = await res.blob();
      const dataUrl = await new Promise((ok, no) => {
        const r = new FileReader();
        r.onload = () => ok(r.result);
        r.onerror = no;
        r.readAsDataURL(blob);
      });

      const sealed = await sealForUpload(dataUrl, key);
      map[id] = await uploadImage(dataUrl, { scope: "owner", kind: "id", sealed });
    } catch {
      /* קובץ שנכשל יישאר גלוי; עדיף מלאבד אותו */
    }
    onProgress?.(++done, ids.length);
  }
  return map;
}

/**
 * מוריד קובץ מוצפן ומחזיר object URL להצגה.
 * המפענח דורש כספת פתוחה — זה הרגע היחיד שבו נדרשת הסיסמה.
 */
export async function openEncryptedFile(id) {
  /* המעטפה נבדקת לפני דרישת המפתח: קובץ שאינו מוצפן (חשבון בלי הצפנה,
     או צילום מלפני ההפעלה) חייב להיטען גם כשהכספת נעולה — אחרת כפתור
     "מוצפן" מוצג על קובץ גלוי לגמרי, והצילום נראה כאילו איננו. */
  const meta = await api.get(`/files/${encodeURIComponent(id)}/envelope`);
  if (!meta.encrypted) return null; // לא מוצפן — נטען כרגיל

  if (!privateKey) throw new Error("locked");

  const res = await fetch(`/api/files/${encodeURIComponent(id)}`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error("הקובץ לא נמצא.");

  const blob = await decryptFile(
    { ciphertext: await res.arrayBuffer(), wrappedKey: meta.wrappedKey, iv: meta.iv },
    privateKey,
    meta.mime,
  );
  return URL.createObjectURL(blob);
}
